import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardHeader, CardTitle, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Phone, Mail, Linkedin, UserPlus, ExternalLink, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const BAND_STYLES = {
  hot: "bg-green-100 text-green-700 border-green-300",
  warm: "bg-amber-100 text-amber-700 border-amber-300",
  cold: "bg-blue-100 text-blue-700 border-blue-300",
};

const CHANNEL_ICONS = { email: Mail, phone: Phone, linkedin: Linkedin };

export default function CompanyContactStrategy({ companyId, company, onAddAsLead, onResearchDMs }) {
  const [research, setResearch] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companyId) return;
    setLoading(true);
    axios.get(`${API}/companies/${companyId}/research`, { withCredentials: true })
      .then(res => setResearch(res.data))
      .catch(() => setResearch(null))
      .finally(() => setLoading(false));
  }, [companyId]);

  const dms = (research?.decision_makers || [])
    .filter(dm => dm.influence_score != null)
    .sort((a, b) => (a.rank || 999) - (b.rank || 999))
    .slice(0, 3);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center justify-between">
          Contact Strategy
          {company?.engagement_band && (
            <Badge className={`capitalize ${BAND_STYLES[company.engagement_band] || ""}`}>
              {company.engagement_band} Company
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : dms.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-sm text-muted-foreground mb-3">No ranked decision makers found</p>
            <Button variant="outline" size="sm" onClick={onResearchDMs}>
              <Search className="h-4 w-4 mr-2" /> Research Decision Makers
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {dms.map((dm, i) => {
              const ChannelIcon = CHANNEL_ICONS[dm.best_contact_channel] || Mail;
              return (
                <div key={dm.id || i} className="flex items-center gap-3 p-2 rounded-lg border bg-muted/30">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold">
                    #{dm.rank || i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm truncate">{dm.name}</span>
                      {dm.is_crm_lead && <Badge variant="outline" className="text-[10px] px-1">In CRM</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{dm.title}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs gap-1">
                      <ChannelIcon className="h-3 w-3" />
                      {dm.influence_score}
                    </Badge>
                    {dm.is_crm_lead ? (
                      <Button variant="ghost" size="sm" className="h-7 text-xs" asChild>
                        <a href={`/leads/${dm.crm_lead_id}`}><ExternalLink className="h-3 w-3" /></a>
                      </Button>
                    ) : (
                      <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => onAddAsLead?.(dm)}>
                        <UserPlus className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
